import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {CwExchangeConfigApiService} from '../../../service/api/cw-exchange-config-api.service';
import {CwExchangeConfigCheckResponseDto} from '../../../model/dto/api/exchange/cw-exchange-config-check-response.dto';
import {CwExchangeConfigCheckFindingDto} from '../../../model/dto/api/exchange/cw-exchange-config-check-finding.dto';
import {CwBiproFlowEnum} from '../../../model/dto/enum/exchange/cw-bipro-flow.enum';
// [CW-SCHEMA] The German operation captions used to be a private map here. They moved to the
// util when the case-side preview picker needed the same words — one operation, one wording.
import {cwExchangeOperationLabel} from '../../../util/cw-exchange-operation.util';

/** operationKey set per CwBiproFlowEnum (spec §4.3 table). */
const SIMPLE_OPERATIONS = ['getQuote', 'getOffer', 'getOrder', 'setOrder'];
const QUALIFIED_OPERATIONS = [...SIMPLE_OPERATIONS, 'getOrderQualified', 'setOrderQualified'];
const QUALIFIED_WITH_QUESTIONS_OPERATIONS = [...QUALIFIED_OPERATIONS, 'getOrderQuestions'];

/**
 * One row of the "Alle Operationen prüfen" roll-up (Rückfrage Q9): how many findings landed on one
 * operation. An EMPTY {@code operationKey} is the CaseType-wide bucket — the skeleton, additional-field
 * and response-mapping rules belong to no single operation.
 */
interface CwConfigCheckRollupRow {
    operationKey: string;
    findingCount: number;
}

/**
 * configCheck report panel (design G.5/§4.3) — the per-rule check of a CaseType's exchange
 * configuration for one operationKey, placed at the CaseType object-component (an "Austausch" action
 * node or an action bar on the BiPRO-Detail node — Slice C wires the host placement).
 *
 * Admin-only tooling (design G.3): calls the read-mostly {@link CwExchangeConfigApiService}, never
 * mutates anything. The "Alle Operationen prüfen" roll-up fires ONE unfiltered configCheck and buckets
 * its findings per operation client-side — see {@link #runAllOperations} for why it may not fan out.
 *
 * CW-EXCHANGE-V2: the coverage figures are gone. They counted registry rows that had both a binding
 * and a skeleton slot, and the registry, the bindings and the slots were all deleted — the check now
 * reports FINDINGS only (one skeleton per CaseType, skeleton parses, additional fields are a valid
 * constant-XOR-logic, response mappings carry the targetKey their targetType needs, …). There is no
 * percentage left to present, so the panel presents none.
 */
@Component({
    selector: 'cw-exchange-config-check-panel',
    templateUrl: './cw-exchange-config-check-panel.component.html',
    styleUrls: ['./cw-exchange-config-check-panel.component.scss'],
    standalone: false,
})
export class CwExchangeConfigCheckPanelComponent implements OnChanges {

    /** The CwCaseType whose exchange configuration is checked (access-checked server-side). */
    @Input() caseTypeId?: number;

    /** The CaseType's BiPRO flow (design G.5) — drives the operationKey selector's option set. */
    @Input() flow: CwBiproFlowEnum = CwBiproFlowEnum.SIMPLE;

    selectedOperationKey = '';
    busy = false;
    result?: CwExchangeConfigCheckResponseDto;
    error?: string;

    rollupBusy = false;
    rollupRows?: CwConfigCheckRollupRow[];
    rollupGreen = false;
    rollupTotalFindingCount = 0;
    rollupError?: string;

    constructor(private api: CwExchangeConfigApiService) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['flow'] || changes['caseTypeId']) {
            const options = this.operationKeyOptions;
            this.selectedOperationKey = options.length ? options[0].value : '';
            this.result = undefined;
            this.error = undefined;
            this.rollupRows = undefined;
            this.rollupGreen = false;
            this.rollupTotalFindingCount = 0;
            this.rollupError = undefined;
        }
    }

    get operationKeys(): string[] {
        switch (this.flow) {
            case CwBiproFlowEnum.QUALIFIED:
                return QUALIFIED_OPERATIONS;
            case CwBiproFlowEnum.QUALIFIED_WITH_QUESTIONS:
                return QUALIFIED_WITH_QUESTIONS_OPERATIONS;
            default:
                return SIMPLE_OPERATIONS;
        }
    }

    get operationKeyOptions(): {label: string; value: string}[] {
        return this.operationKeys.map(key => ({label: cwExchangeOperationLabel(key), value: key}));
    }

    /** The empty key is the CaseType-wide bucket of the roll-up, not an operation. */
    operationLabel(operationKey: string): string {
        if (!operationKey) {
            return 'Vorgangstyp-weit';
        }
        return cwExchangeOperationLabel(operationKey);
    }

    // ============================================================================================
    // Single-operation check
    // ============================================================================================

    runCheck(): void {
        if (this.caseTypeId == null || !this.selectedOperationKey) {
            this.error = 'Bitte einen Antragstyp und eine Operation angeben.';
            return;
        }
        this.busy = true;
        this.error = undefined;
        this.api.configCheck({caseTypeId: this.caseTypeId, operationKey: this.selectedOperationKey}).subscribe({
            next: res => {
                this.result = res;
                this.busy = false;
            },
            error: (err: HttpErrorResponse) => {
                this.busy = false;
                this.error = this.extractError(err);
            },
        });
    }

    // ============================================================================================
    // "Alle Operationen prüfen" roll-up (Rückfrage Q9)
    // ============================================================================================

    /**
     * Runs ONE unfiltered configCheck and buckets its findings per operation.
     *
     * It deliberately does NOT fire one call per operationKey: the backend's operationKey filter narrows
     * only the per-definition rules, while the skeleton, additional-field and response-mapping rules are
     * CaseType-wide and run on EVERY call. Fanning out would therefore report each of those findings
     * once per operation and sum them into a figure several times too large.
     */
    runAllOperations(): void {
        if (this.caseTypeId == null) {
            this.rollupError = 'Bitte einen Antragstyp angeben.';
            return;
        }
        this.rollupBusy = true;
        this.rollupError = undefined;
        this.rollupRows = undefined;

        this.api.configCheck({caseTypeId: this.caseTypeId}).subscribe({
            next: report => {
                this.rollupBusy = false;
                this.applyRollup(report);
            },
            error: (err: HttpErrorResponse) => {
                this.rollupBusy = false;
                this.rollupError = this.extractError(err);
            },
        });
    }

    private applyRollup(report: CwExchangeConfigCheckResponseDto): void {
        const findings = report.findings ?? [];
        this.rollupGreen = report.green;
        this.rollupTotalFindingCount = findings.length;

        // The expected operations and the CaseType-wide bucket are pre-seeded at zero: a missing row
        // would read as "not checked" rather than "checked, nothing found".
        const counts = new Map<string, number>();
        counts.set('', 0);
        this.operationKeys.forEach(operationKey => counts.set(operationKey, 0));
        findings.forEach(finding => {
            const key = finding.operationKey ?? '';
            counts.set(key, (counts.get(key) ?? 0) + 1);
        });

        this.rollupRows = Array.from(counts, ([operationKey, findingCount]) => ({operationKey, findingCount}));
    }

    // ============================================================================================
    // Findings table
    // ============================================================================================

    severitySeverity(severity: string): 'danger' | 'warn' | 'info' | 'secondary' {
        switch ((severity ?? '').toUpperCase()) {
            case 'ERROR':
                return 'danger';
            case 'WARNING':
                return 'warn';
            case 'INFO':
                return 'info';
            default:
                return 'secondary';
        }
    }

    trackByRuleId(_index: number, finding: CwExchangeConfigCheckFindingDto): string {
        return `${finding.ruleId}|${finding.operationKey ?? ''}|${finding.fieldKey ?? ''}|${finding.detail}`;
    }

    trackByOperationKey(_index: number, row: CwConfigCheckRollupRow): string {
        return row.operationKey;
    }

    private extractError(err: HttpErrorResponse): string {
        return err?.error?.error || err?.error?.message || err?.message
            || 'Die Konfigurationsprüfung konnte nicht ausgeführt werden.';
    }
}
